import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useSession } from "@/hooks/use-session";
import { EmptyState } from "@/components/system";
import { formatShortDate } from "@/lib/date-format";
import { TrainingLoadChart } from "@/components/health/TrainingLoadChart";

/**
 * /health/training — sub-area home (SPEC 3.3.3 + BW20-I sport categorization).
 * Shows recent sessions with sport name + category, category breakdown panel,
 * and category filter chips.
 */

type SportRow = {
  id: string;
  name: string;
  category: string;
};

const CATEGORY_COLORS: Record<string, string> = {
  endurance: "var(--accent)",
  strength: "var(--amber)",
  class: "var(--plum)",
  flexibility: "var(--teal)",
  mobility: "var(--teal)",
  recreation: "var(--leaf)",
  recovery: "var(--brick)",
};

const CATEGORY_LABELS: Record<string, string> = {
  endurance: "Endurance",
  strength: "Strength",
  class: "Class",
  flexibility: "Flexibility",
  mobility: "Mobility",
  recreation: "Recreation",
  recovery: "Recovery",
};

export function HealthTraining() {
  const { session } = useSession();
  const userId = session?.user?.id;
  const [categoryFilter, setCategoryFilter] = useState<string | null>(null);

  const sessions = useQuery({
    queryKey: ["health_training_sessions", userId],
    enabled: Boolean(userId),
    staleTime: 60_000,
    queryFn: async () => {
      if (!userId) return [];
      const since = new Date();
      since.setDate(since.getDate() - 14);
      const { data, error } = await supabase
        .from("session")
        .select("id, sport_id, start_ts, end_ts, duration_min, distance_km, kcal, avg_hr_bpm, rpe, name")
        .eq("user_id", userId)
        .gte("end_ts", since.toISOString())
        .order("end_ts", { ascending: false });
      if (error) throw error;
      return data ?? [];
    },
  });

  const sports = useQuery({
    queryKey: ["sports", userId],
    enabled: Boolean(userId),
    staleTime: 300_000,
    queryFn: async (): Promise<SportRow[]> => {
      if (!userId) return [];
      const { data, error } = await supabase
        .from("sport")
        .select("id, name, category")
        .eq("user_id", userId);
      if (error) throw error;
      return (data ?? []) as SportRow[];
    },
  });

  const sportMap = new Map((sports.data ?? []).map((s) => [s.id, s]));

  const allSessions = sessions.data ?? [];

  const categoryTotals = allSessions.reduce<Record<string, { count: number; minutes: number }>>(
    (acc, s) => {
      const cat = sportMap.get(s.sport_id)?.category ?? "other";
      if (!acc[cat]) acc[cat] = { count: 0, minutes: 0 };
      acc[cat].count++;
      acc[cat].minutes += Math.round(Number(s.duration_min));
      return acc;
    },
    {},
  );

  const categoriesPresent = Object.keys(categoryTotals).sort();

  const filtered = allSessions.filter((s) => {
    if (!categoryFilter) return true;
    return (sportMap.get(s.sport_id)?.category ?? "other") === categoryFilter;
  });

  return (
    <article aria-labelledby="training-heading">
      <p
        style={{
          fontFamily: "var(--font-sans)",
          fontSize: "var(--type-micro)",
          letterSpacing: "0.06em",
          textTransform: "uppercase",
          color: "var(--fg-3)",
          margin: 0,
        }}
      >
        <Link to="/health" style={{ color: "var(--fg-3)" }}>
          Health
        </Link>{" "}
        · Training
      </p>
      <h1
        id="training-heading"
        style={{
          fontFamily: "var(--font-serif)",
          fontStyle: "italic",
          fontWeight: 500,
          fontSize: "var(--type-display-m)",
          margin: "var(--space-2) 0 var(--space-8)",
        }}
      >
        What you've done.
      </h1>

      <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-6)" }}>
        <TrainingLoadChart />

        {categoriesPresent.length > 0 && (
          <section
            style={{
              background: "var(--bg-2)",
              border: "1px solid var(--hair)",
              borderRadius: "var(--radius-card)",
              padding: "var(--space-4)",
            }}
          >
            <h3
              style={{
                margin: 0,
                marginBottom: "var(--space-3)",
                fontSize: "var(--type-body)",
                fontWeight: 500,
              }}
            >
              14-day breakdown
            </h3>
            <div style={{ display: "flex", gap: "var(--space-3)", flexWrap: "wrap" }}>
              {categoriesPresent.map((cat) => {
                const totals = categoryTotals[cat];
                const color = CATEGORY_COLORS[cat] ?? "var(--fg-3)";
                const isActive = categoryFilter === cat;
                return (
                  <button
                    key={cat}
                    type="button"
                    onClick={() => setCategoryFilter(isActive ? null : cat)}
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 2,
                      padding: "var(--space-3) var(--space-4)",
                      background: isActive ? `color-mix(in oklab, ${color} 12%, transparent)` : "var(--bg-3)",
                      border: `1px solid ${isActive ? color : "var(--hair)"}`,
                      borderRadius: "var(--radius-card)",
                      cursor: "pointer",
                      textAlign: "left",
                      minWidth: 90,
                    }}
                  >
                    <span
                      style={{
                        fontSize: "var(--type-micro)",
                        letterSpacing: "0.06em",
                        textTransform: "uppercase",
                        color,
                      }}
                    >
                      {CATEGORY_LABELS[cat] ?? cat}
                    </span>
                    <span style={{ fontSize: "var(--type-body-s)", fontWeight: 500, color: "var(--fg)" }}>
                      {totals.count}× · {totals.minutes}min
                    </span>
                  </button>
                );
              })}
            </div>
          </section>
        )}

        <article
          style={{
            background: "var(--bg-2)",
            border: "1px solid var(--hair)",
            borderRadius: "var(--radius-card)",
            padding: "var(--space-4)",
          }}
        >
          <h3 style={{ margin: 0, marginBottom: "var(--space-3)", fontSize: "var(--type-body)" }}>
            Recent sessions · last 14 days
            {categoryFilter && (
              <span style={{ color: "var(--fg-3)", fontWeight: 400, marginLeft: 8, fontSize: "var(--type-meta)" }}>
                {CATEGORY_LABELS[categoryFilter] ?? categoryFilter}
              </span>
            )}
          </h3>
          {sessions.isLoading ? (
            <p style={{ color: "var(--fg-3)", fontSize: "var(--type-body-s)" }}>Loading…</p>
          ) : filtered.length === 0 ? (
            <EmptyState
              headline="No sessions yet."
              sub="Sessions sync from Strava every 15 minutes, plus instantly when Strava pushes a new activity. Manual workouts log via Today."
            />
          ) : (
            <ul style={{ margin: 0, padding: 0, listStyle: "none", display: "grid", gap: 8 }}>
              {filtered.map((s) => {
                const sport = sportMap.get(s.sport_id);
                const catColor = CATEGORY_COLORS[sport?.category ?? ""] ?? "var(--fg-3)";
                return (
                  <li
                    key={s.id}
                    style={{
                      display: "grid",
                      gridTemplateColumns: "auto 1fr auto",
                      gap: "var(--space-3)",
                      alignItems: "baseline",
                      fontSize: "var(--type-body-s)",
                      color: "var(--fg-2)",
                      padding: "6px 0",
                      borderBottom: "1px solid var(--hair)",
                    }}
                  >
                    <span className="mono" style={{ color: "var(--fg-3)" }}>
                      {formatShortDate(s.start_ts)}
                    </span>
                    <span style={{ color: "var(--fg)", display: "flex", alignItems: "baseline", gap: 6, flexWrap: "wrap" }}>
                      {s.name || sport?.name || s.sport_id.replace(/_/g, " ")}
                      {sport?.category && (
                        <span
                          style={{
                            fontSize: "var(--type-micro)",
                            letterSpacing: "0.04em",
                            textTransform: "uppercase",
                            color: catColor,
                          }}
                        >
                          {CATEGORY_LABELS[sport.category] ?? sport.category}
                        </span>
                      )}
                      {s.rpe ? (
                        <span style={{ color: "var(--fg-3)", fontSize: "var(--type-meta)" }}>
                          RPE {s.rpe}
                        </span>
                      ) : null}
                    </span>
                    <span className="mono" style={{ color: "var(--fg-3)" }}>
                      {Math.round(Number(s.duration_min))}min
                      {s.distance_km ? ` · ${Number(s.distance_km).toFixed(1)}km` : ""}
                    </span>
                  </li>
                );
              })}
            </ul>
          )}
        </article>
      </div>
    </article>
  );
}
